import { Dispatch } from 'redux';
import API from 'services/api';
import showErrorAlert, { parseError } from 'services/errorHandler';
import sessionService from 'services/session';
import { getRouteWithToken, redirectToError } from 'utilities';
import { getHostedPageType } from 'utilities/global';
import history from 'shared/services/history';
import * as CONSTS from 'redux/constants';
import {
  APP_ROUTE_NOT_FOUND,
  CARD_ISSUING_ACTIVATE_THANK_YOU,
  CARD_ISSUING_ERROR,
  CARD_ISSUING_SET_KBA_THANK_YOU
} from 'routes/paths';
import { HOSTED_PAGE_TYPES, STATUSES, SUCCESS_CODE } from 'consts';
import { sendGetPageFailMessage } from 'services/iframeService';
import { displaySpinner, hideSpinner } from 'redux/actions/global';

interface ISetKbaProps {
  token: string;
  formData: Record<string, any>;
}

const getSetKbaPageStart = () => ({
  type: CONSTS.GET_SET_KBA_PAGE_START
});

const getSetKbaPageSuccess = (data: Record<string, any>) => ({
  type: CONSTS.GET_SET_KBA_PAGE_SUCCESS,
  payload: { data }
});

const getSetKbaPageFail = (
  error: string,
  data: Record<string, any> | null | undefined
) => ({
  type: CONSTS.GET_SET_KBA_PAGE_FAIL,
  payload: { error, data }
});

export const getSetKbaPage = (token: string) => async (dispatch: Dispatch<any>) => {
  const handleGetPageError = (error: Record<string, any>, url = '') => {
    const { status, data } = parseError(error);
    dispatch(getSetKbaPageFail(status?.error_code, data));
    redirectToError({ error, url });
  };

  if (!token) {
    sendGetPageFailMessage(HOSTED_PAGE_TYPES.cardIssuingSetKba)();
    return redirectToError({ url: APP_ROUTE_NOT_FOUND });
  }

  try {
    dispatch(displaySpinner());
    dispatch(getSetKbaPageStart());

    let response;
    try {
      response = await API.makeApiCall({
        method: 'get',
        url: `hosted/issuing/kba/${token}`,
        pageToken: token
      });
    } catch (error) {
      const isRedirected = sessionService.handleUnauthorizedError(error, token);
      if (!isRedirected) {
        handleGetPageError(error, APP_ROUTE_NOT_FOUND);
      }
      return;
    }

    const { data: pageData } = response.data || {};
    dispatch(getSetKbaPageSuccess(pageData));
    const { status } = pageData || {};
    if (
      status === STATUSES.done &&
      !window?.location?.pathname.includes(CARD_ISSUING_SET_KBA_THANK_YOU)
    ) {
      history.push(getRouteWithToken(CARD_ISSUING_SET_KBA_THANK_YOU, token));
    }
  } catch (error) {
    handleGetPageError(error, CARD_ISSUING_ERROR);
  } finally {
    dispatch(hideSpinner());
  }
};

const setKbaStart = () => ({
  type: CONSTS.SET_KBA_START
});

const setKbaSuccess = () => ({
  type: CONSTS.SET_KBA_SUCCESS
});

const setKbaFail = (error: string) => ({
  type: CONSTS.SET_KBA_FAIL,
  payload: { error }
});

export const setKba =
  ({ token, formData }: ISetKbaProps) =>
  async (dispatch: Dispatch<any>) => {
    try {
      dispatch(setKbaStart());
      const response = await API.makeApiCall({
        method: 'post',
        url: `hosted/issuing/${token}/set_kba`,
        data: formData,
        pageToken: token
      });

      const {
        status: { status }
      } = response.data || {};
      if (status === SUCCESS_CODE) {
        dispatch(setKbaSuccess());
        const pageType = getHostedPageType();
        const routeByPageType = {
          [HOSTED_PAGE_TYPES.cardIssuingActivate]: CARD_ISSUING_ACTIVATE_THANK_YOU,
          [HOSTED_PAGE_TYPES.cardIssuingSetKba]: CARD_ISSUING_SET_KBA_THANK_YOU
        };
        history.push(getRouteWithToken(routeByPageType[pageType], token));
      } else {
        showErrorAlert({ error: 'ERROR_SET_KBA' });
      }
    } catch (error) {
      const { status } = parseError(error);
      const { error_code: errorCode } = status || {};
      dispatch(setKbaFail(errorCode));
      const isRedirected = sessionService.handleUnauthorizedError(error, token);
      if (!isRedirected) {
        showErrorAlert({ error: 'ERROR_SET_KBA' });
      }
    }
  };
